// src/pages/Abonnement.js
// =====================================================
// Page Plans d'abonnement + formulaire upload reçu
// → NE TOUCHE À RIEN dans le reste du code
// Ajouter dans App.js :  import Abonnement from "./pages/Abonnement";
//                        <Route path="/abonnement" element={<Abonnement />} />
// =====================================================

import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import * as api from "../api";

const PLANS = [
  {
    id: "gratuit",
    label: "FREE",
    sous_titre: "Pour démarrer",
    couleur: "#6b7280",
    badge: null,
    prix: "Gratuit",
    annonces: 2,
    features: [
      "2 annonces maximum",
      "3 photos par annonce",
      "Contact par message",
      "Visibilité standard",
    ],
    disabled_features: [
      "Badge Pro",
      "Mise en avant",
      "Statistiques",
      "Support rapide",
    ],
  },
  {
    id: "premium_starter",
    label: "PREMIUM STARTER",
    sous_titre: "Petits business sérieux",
    couleur: "#0d9488",
    badge: "Populaire",
    prix: "2 000 DA",
    annonces: 10,
    features: [
      "10 annonces",
      "Photos illimitées",
      "Badge « Pro »",
      "Meilleur classement",
      "Statistiques simples",
    ],
    disabled_features: [
      "Mise en avant homepage",
      "Statistiques avancées",
      "Support prioritaire",
    ],
  },
  {
    id: "premium_business",
    label: "PREMIUM BUSINESS",
    sous_titre: "Magasins & entreprises",
    couleur: "#2563eb",
    badge: "Recommandé",
    prix: " 6 000 DA",
    annonces: 30,
    features: [
      "30 annonces",
      "Mise en avant homepage",
      "Statistiques avancées",
      "Badge Pro",
      "Support rapide",
    ],
    disabled_features: [
      "Annonces illimitées",
      "Top visibilité",
      "Mini-site profil",
    ],
  },
  {
    id: "premium_enterprise",
    label: "ENTERPRISE",
    sous_titre: "Clubs & écoles nautiques",
    couleur: "#7c3aed",
    badge: "Premium",
    prix: "15 000 DA",
    annonces: "Illimitées",
    features: [
      "Annonces illimitées",
      "Top visibilité (toujours en haut)",
      "Mini-site profil professionnel",
      "Badge « Entreprise Vérifiée »",
      "Activités & événements",
      "Support prioritaire dédié",
    ],
    disabled_features: [],
  },
];

export default function Abonnement() {
  const { user } = useAuth();
  const [planChoisi, setPlanChoisi] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [monAbonnement, setMonAbonnement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [erreur, setErreur] = useState("");

  const [form, setForm] = useState({
    methode_paiement: "CCP",
    date_paiement: "",
    image_recu_url: "",
    montant: "",
  });
  const [uploadingRecu, setUploadingRecu] = useState(false);

  useEffect(() => {
    if (user?.role === "exposant") {
      api.getMonAbonnement()
        .then(r => setMonAbonnement(r.data))
        .catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <p>Connectez-vous pour accéder aux plans.</p>
        </div>
        <Footer />
      </>
    );
  }

  const handleChoisirPlan = (plan) => {
    if (plan.id === "gratuit") return;
    setPlanChoisi(plan);
    setShowForm(true);
    setSuccess(false);
    setErreur("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUploadRecu = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("files", file);
    setUploadingRecu(true);
    try {
      const res = await api.uploadFiles(formData);
      setForm(f => ({ ...f, image_recu_url: res.data.urls[0] }));
    } catch {
      setErreur("Erreur lors de l'upload du reçu.");
    } finally {
      setUploadingRecu(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.image_recu_url) return setErreur("Veuillez uploader votre reçu.");
    if (!form.date_paiement)  return setErreur("Veuillez indiquer la date de paiement.");
    setLoading(true);
    setErreur("");
    try {
      await api.soumettreAbonnement({
        plan: planChoisi.id,
        methode_paiement: form.methode_paiement,
        image_recu_url: form.image_recu_url,
        date_paiement: form.date_paiement,
        montant: form.montant || null,
      });
      setSuccess(true);
      setShowForm(false);
      // Rafraîchir le statut
      const r = await api.getMonAbonnement();
      setMonAbonnement(r.data);
    } catch (e) {
      setErreur(e.response?.data?.message || "Erreur lors de la soumission.");
    } finally {
      setLoading(false);
    }
  };

  const planActuel = monAbonnement?.plan_actuel || user.plan || "gratuit";
  const expireDate = monAbonnement?.expire_le ? new Date(monAbonnement.expire_le) : null;
  // Un compte est actif si : plan non gratuit ET (date d'expiration future OU dernière entrée ACTIVE dans historique)
  const dernierActifHisto = monAbonnement?.historique?.find(h => h.statut === "ACTIVE");
  const abonnementActif = planActuel !== "gratuit" && (
    (expireDate && expireDate > new Date()) ||
    (dernierActifHisto != null)  // fallback si expire_le encore en cours de calcul
  );
  const daysLeft = expireDate && expireDate > new Date()
    ? Math.ceil((expireDate - new Date()) / (1000 * 60 * 60 * 24))
    : 30; // fallback affiché si expire_le vient juste d'être calculé
  const planActuelInfo = PLANS.find(p => p.id === planActuel);
  const dernierePending = monAbonnement?.historique?.find(h => h.statut === "PENDING");
  const derniereRejeted = monAbonnement?.historique?.find(h => h.statut === "REJECTED");

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 60px" }}>

        {/* ✅ BANNIÈRE ABONNEMENT ACTIF — claire et visible */}
        {abonnementActif && (
          <div style={{
            background: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
            borderRadius: 16, padding: "28px 32px", marginBottom: 32,
            color: "white", boxShadow: "0 4px 24px rgba(13,148,136,0.3)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                  ⭐
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.8, marginBottom: 4 }}>
                    Compte Premium actif
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>
                    {planActuelInfo?.label || planActuel}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
                    Expire le <strong>{expireDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</strong>
                    {" "}·{" "}
                    <strong>{daysLeft} jour{daysLeft > 1 ? "s" : ""}</strong> restant{daysLeft > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 600 }}>
                  ✓ Annonces actives : <strong>{planActuelInfo?.annonces || "—"}</strong>
                </div>
                <div style={{ marginTop: 10, height: 6, background: "rgba(255,255,255,0.2)", borderRadius: 6, overflow: "hidden", minWidth: 200 }}>
                  <div style={{ height: "100%", background: daysLeft > 10 ? "#86efac" : "#fca5a5", width: `${Math.min(100, (daysLeft / 30) * 100)}%`, borderRadius: 6, transition: "width 0.3s" }} />
                </div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                  {daysLeft > 10 ? "Abonnement en bonne santé" : "⚠️ Pensez à renouveler bientôt"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Titre */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
            Plans d'abonnement
          </h1>
          <p style={{ color: "#64748b", fontSize: 15 }}>
            Choisissez le plan adapté à votre activité et soumettez votre reçu de paiement.
          </p>
        </div>

        {/* Alerte PENDING */}
        {dernierePending && !success && (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "14px 18px", marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 20 }}>⏳</span>
            <div>
              <strong>Demande en cours de vérification</strong>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#92400e" }}>
                Votre reçu pour le plan <strong>{PLANS.find(p => p.id === dernierePending.plan)?.label}</strong> est en attente de validation par l'administrateur. Vous serez notifié(e) par email dès la confirmation.
              </p>
            </div>
          </div>
        )}

        {/* Alerte REJECTED — seulement si pas d'abonnement actif */}
        {derniereRejeted && !dernierePending && !success && !abonnementActif && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px 18px", marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 20 }}>❌</span>
            <div>
              <strong>Paiement refusé</strong>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#b91c1c" }}>
                {derniereRejeted.note_admin || "Veuillez corriger votre paiement et soumettre un nouveau reçu."}
              </p>
            </div>
          </div>
        )}

        {/* Succès */}
        {success && (
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "16px 20px", marginBottom: 24, display: "flex", gap: 12 }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div>
              <strong>Demande envoyée avec succès !</strong>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#15803d" }}>
                Votre reçu a été soumis. L'administrateur va le vérifier et vous recevrez une notification dès la validation.
              </p>
            </div>
          </div>
        )}

        {/* Formulaire de paiement */}
        {showForm && planChoisi && (
          <div style={{ background: "#fff", border: "2px solid " + planChoisi.couleur, borderRadius: 14, padding: 28, marginBottom: 36 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: planChoisi.couleur }}>
                Paiement — {planChoisi.label}
              </h2>
              <button onClick={() => { setShowForm(false); setPlanChoisi(null); }}
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>

            {/* Instructions */}
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#475569" }}>
              <strong>Instructions de paiement :</strong>
              <ol style={{ marginTop: 8, paddingLeft: 18, lineHeight: 1.8 }}>
                <li>Effectuez le virement sur <strong>CCP N° [00799999004168591405]</strong> ou via <strong>BaridiMob</strong></li>
                <li>Montant : <strong>{planChoisi.prix}</strong> / mois</li>
                <li>Uploadez une photo de votre reçu ci-dessous</li>
                <li>L'admin validera sous 24–48h</li>
              </ol>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {/* Méthode */}
              <div>
                <label style={labelStyle}>Méthode de paiement *</label>
                <select value={form.methode_paiement}
                  onChange={e => setForm(f => ({ ...f, methode_paiement: e.target.value }))}
                  style={inputStyle}>
                  <option value="CCP">CCP</option>
                  <option value="BaridiMob">BaridiMob</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label style={labelStyle}>Date du paiement *</label>
                <input type="date" value={form.date_paiement}
                  onChange={e => setForm(f => ({ ...f, date_paiement: e.target.value }))}
                  style={inputStyle} />
              </div>

              {/* Montant */}
              <div>
                <label style={labelStyle}>Montant payé (DA)</label>
                <input type="number" placeholder="ex: 2000"
                  value={form.montant}
                  onChange={e => setForm(f => ({ ...f, montant: e.target.value }))}
                  style={inputStyle} />
              </div>

              {/* Upload reçu */}
              <div>
                <label style={labelStyle}>Reçu de paiement (photo) *</label>
                <input type="file" accept="image/*" onChange={handleUploadRecu}
                  style={{ ...inputStyle, padding: "6px 8px", cursor: "pointer" }} />
                {uploadingRecu && <p style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Upload en cours...</p>}
                {form.image_recu_url && !uploadingRecu && (
                  <p style={{ fontSize: 12, color: "#16a34a", marginTop: 4 }}>✅ Reçu uploadé</p>
                )}
              </div>
            </div>

            {erreur && (
              <p style={{ color: "#dc2626", fontSize: 13, marginTop: 12 }}>⚠️ {erreur}</p>
            )}

            <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
              <button onClick={handleSubmit} disabled={loading || uploadingRecu}
                style={{ padding: "11px 28px", background: planChoisi.couleur, color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
                {loading ? "Envoi..." : "Soumettre ma demande"}
              </button>
              <button onClick={() => { setShowForm(false); setPlanChoisi(null); }}
                style={{ padding: "11px 20px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 8, cursor: "pointer" }}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Grille des plans */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 20 }}>
          {PLANS.map(plan => {
            const estActuel = planActuel === plan.id;
            const estGratuit = plan.id === "gratuit";
            return (
              <div key={plan.id}
                style={{
                  background: "#fff",
                  border: estActuel ? `2px solid ${plan.couleur}` : "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: 24,
                  position: "relative",
                  boxShadow: estActuel ? `0 0 0 3px ${plan.couleur}22` : "0 1px 4px rgba(0,0,0,0.06)",
                  transition: "box-shadow 0.2s"
                }}>

                {/* Badge */}
                {plan.badge && (
                  <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: plan.couleur, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 14px", borderRadius: 20 }}>
                    {plan.badge}
                  </div>
                )}

                {/* Badge Actif — seulement si c'est vraiment le plan actif ET abonnement valide */}
                {estActuel && abonnementActif && (
                  <div style={{ position: "absolute", top: 12, right: 12, background: "#0d9488", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20, boxShadow: "0 2px 8px rgba(13,148,136,0.4)" }}>
                    ✓ Actif
                  </div>
                )}
                {estActuel && !abonnementActif && plan.id !== "gratuit" && (
                  <div style={{ position: "absolute", top: 12, right: 12, background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20 }}>
                    Expiré
                  </div>
                )}

                <div style={{ color: plan.couleur, fontWeight: 800, fontSize: 13, letterSpacing: 1, marginBottom: 4 }}>
                  {plan.label}
                </div>
                <div style={{ color: "#64748b", fontSize: 12, marginBottom: 14 }}>{plan.sous_titre}</div>

                <div style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>{plan.prix}</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>/ mois</div>

                <div style={{ fontSize: 12, color: "#475569", marginBottom: 16 }}>
                  <strong>{plan.annonces}</strong> annonces
                </div>

                {/* Features */}
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px", fontSize: 13 }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, color: "#374151" }}>
                      <span style={{ color: plan.couleur, flexShrink: 0 }}>✓</span> {f}
                    </li>
                  ))}
                  {plan.disabled_features.map((f, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, color: "#cbd5e1" }}>
                      <span style={{ flexShrink: 0 }}>✗</span> {f}
                    </li>
                  ))}
                </ul>

                {/* Bouton */}
                {!estGratuit && user.role === "exposant" && (
                  <button
                    onClick={() => handleChoisirPlan(plan)}
                    disabled={estActuel && abonnementActif}
                    style={{
                      width: "100%", padding: "10px 0",
                      background: estActuel && abonnementActif ? "#f1f5f9" : plan.couleur,
                      color: estActuel && abonnementActif ? "#94a3b8" : "#fff",
                      border: "none", borderRadius: 8,
                      fontWeight: 600, fontSize: 13,
                      cursor: estActuel && abonnementActif ? "default" : "pointer"
                    }}>
                    {estActuel && abonnementActif ? "✓ Plan actuel" : estActuel ? "Renouveler" : "Choisir ce plan"}
                  </button>
                )}
                {estGratuit && (
                  <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>
                    Plan par défaut
                  </div>
                )}
                {!estGratuit && user.role !== "exposant" && (
                  <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 12, padding: "8px 0" }}>
                    Réservé aux exposants
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Historique */}
        {monAbonnement?.historique?.length > 0 && (
          <div style={{ marginTop: 48 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", marginBottom: 16 }}>
              Historique des demandes
            </h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    {["Plan", "Méthode", "Date paiement", "Soumis le", "Statut", "Note"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monAbonnement.historique.map(h => (
                    <tr key={h.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 12px" }}>{PLANS.find(p => p.id === h.plan)?.label || h.plan}</td>
                      <td style={{ padding: "10px 12px" }}>{h.methode_paiement}</td>
                      <td style={{ padding: "10px 12px" }}>{new Date(h.date_paiement).toLocaleDateString("fr-FR")}</td>
                      <td style={{ padding: "10px 12px" }}>{new Date(h.created_at).toLocaleDateString("fr-FR")}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{
                          padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: h.statut === "ACTIVE" ? "#f0fdf4" : h.statut === "PENDING" ? "#fffbeb" : "#fef2f2",
                          color: h.statut === "ACTIVE" ? "#16a34a" : h.statut === "PENDING" ? "#b45309" : "#dc2626",
                        }}>
                          {h.statut === "ACTIVE" ? "✅ Actif" : h.statut === "PENDING" ? "⏳ En attente" : "❌ Refusé"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#64748b" }}>{h.note_admin || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, boxSizing: "border-box", background: "#fff" };